import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

type Props = {
	icon: ReactNode;
	heading: string;
	description?: ReactNode;
	rightActions?: ReactNode;
	onClick?: () => void;
	testId?: string;
	hasBoxShadow?: boolean;
};

export const Card = ({
	icon,
	rightActions,
	onClick,
	heading,
	description,
	testId,
	hasBoxShadow = true,
}: Props) => {
	const content = (
		<>
			<IconWrapper>{icon}</IconWrapper>
			<ContentWrapper>
				<Header>{heading}</Header>
				{description && <Description>{description}</Description>}
			</ContentWrapper>
			{rightActions && <ActionWrapper>{rightActions}</ActionWrapper>}
		</>
	);

	return onClick ? (
		<ButtonWrapper onClick={onClick} data-testid={testId}>
			{content}
		</ButtonWrapper>
	) : (
		<Wrapper data-testid={testId} hasBoxShadow={hasBoxShadow}>
			{content}
		</Wrapper>
	);
};

const commonWrapperStyling = {
	display: 'flex',
	flexFlow: 'row',
	alignItems: 'center',
	borderRadius: '8px',
	marginTop: token('space.100', '8px'),
	position: 'relative',
	width: '100%',
	minHeight: token('space.1000', '80px'),
	boxShadow: token(
		'elevation.shadow.raised',
		'0px 1px 1px rgba(9, 30, 66, 0.25),0px 0px 1px rgba(9, 30, 66, 0.31)',
	),
	backgroundColor: `${token('elevation.surface', colors.N0)}`,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ hasBoxShadow?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...commonWrapperStyling,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: ({ hasBoxShadow }) =>
		hasBoxShadow
			? token(
					'elevation.shadow.raised',
					'0px 1px 1px rgba(9, 30, 66, 0.25),0px 0px 1px rgba(9, 30, 66, 0.31)',
				)
			: `inset 0 0 0 1px ${token('color.border.disabled')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.button({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...commonWrapperStyling,
	border: 'none',
	padding: 0,
	cursor: 'pointer',
	textAlign: 'left',

	'&:hover': {
		boxShadow: token(
			'elevation.shadow.overflow',
			'0px 0px 8px rgba(9, 30, 66, 0.16),0px 0px 1px rgba(9, 30, 66, 0.12)',
		),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'absolute',
	width: token('space.1000', '80px'),
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N20),
	borderTopLeftRadius: token('space.100', '8px'),
	borderBottomLeftRadius: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	flex: 1,
	padding: `${token('space.250', '20px')} ${token('space.200', '16px')}`,
	textOverflow: 'ellipsis',
	justifyContent: 'start',
	marginLeft: token('space.1000', '80px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.span({
	display: 'block',
	font: token('font.heading.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	display: 'flex',
	alignItems: 'flex-start',
	font: token('font.body.UNSAFE_small'),
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	whiteSpace: 'pre-wrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionWrapper = styled.div({
	width: '15%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	minWidth: token('space.600', '48px'),
});
